<template>
  <div
    v-if="deliveryRequest.isLoading"
    class="d-flex flex-column align-center py-12"
  >
    <v-progress-circular
      :size="70"
      color="primary"
      :width="7"
      indeterminate
    />
  </div>
  <div v-else>
    <div class="d-flex align-center">
      <h2 class="primary--text mb-3">
        <v-btn
          color="primary"
          icon
          @click="$router.push('/delivery-requests')"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        {{ deliveryRequest.details.deliveryRequestId }}
      </h2>

      <v-spacer />

      <v-btn
        :loading="isDownloadingRoutesCsv"
        depressed
        color="primary"
        class="text-capitalize"
        @click="downloadRoutesCsv"
      >
        <v-icon
          small
          left
        >
          mdi-download
        </v-icon>
        Download Routes CSV
      </v-btn>

      <v-btn
        :loading="isDownloadingBNQCsv"
        depressed
        color="primary"
        class="mx-3 text-capitalize"
        @click="downloadBNQCsv"
      >
        <v-icon
          small
          left
        >
          mdi-download
        </v-icon>
        Download B&Q Bookings CSV
      </v-btn>
    </div>

    <v-row>
      <v-col cols="6">
        <div class="d-flex">
          <div class="flex-grow-1">
            <div
              :class="isAccepted ? 'primary white--text' : 'grey lighten-2'"
              class="flex-grow-1 pa-3 d-flex align-center pointed-end"
            >
              <v-icon
                class="mr-2"
                :dark="isAccepted"
              >
                mdi-truck
              </v-icon>
              Request Accepted
            </div>

            <div class="pt-1 d-flex primary" />
          </div>

          <div :class="isAccepted ? 'pointed-end-primary' : 'pointed-end-grey'" />
        </div>
      </v-col>

      <!-- <v-col cols="4">
        <div
          :class="isAccepted ? 'grey white--text' : 'grey lighten-3'"
          class="grey pa-3 d-flex align-ceter"
        >
          <v-icon class="mr-1" :dark="isAccepted">mdi-truck-fast</v-icon>In Progress
        </div>
        <div class="pt-1 d-flex warning"></div>
      </v-col> -->

      <v-col cols="6">
        <div class="d-flex">
          <div class="flex-grow-1">
            <div
              :class="isDelivered ? 'green white--text' : 'grey lighten-2'"
              class="pa-3 d-flex align-center pointed-end"
            >
              <v-icon
                class="mr-2"
                :dark="isDelivered"
              >
                mdi-truck-check
              </v-icon>
              Shipment Delivered
            </div>

            <div class="pt-1 d-flex green" />
          </div>

          <div :class="isDelivered ? 'pointed-end-success' : 'pointed-end-grey'" />
        </div>
      </v-col>

      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="d-flex">
            <v-list-item two-line>
              <v-list-item-content>
                <v-list-item-subtitle>ID</v-list-item-subtitle>
                <v-list-item-title>
                  {{
                    deliveryRequest.details.deliveryRequestId || "N/A"
                  }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item two-line>
              <v-list-item-content>
                <v-list-item-subtitle>Request Date/Time</v-list-item-subtitle>
                <v-list-item-title>
                  {{
                    humanizeDateFromSeconds(
                      deliveryRequest.details.createdAt,
                      "MMMM Do YYYY, h:mm A"
                    ) || "N/A"
                  }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item two-line>
              <v-list-item-content>
                <v-list-item-subtitle>Number of Routes</v-list-item-subtitle>
                <v-list-item-title>
                  {{
                    deliveryRequest.details.numberOfRoutes || 0
                  }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item two-line>
              <v-list-item-content>
                <v-list-item-subtitle>
                  Number of completed routes
                </v-list-item-subtitle>
                <v-list-item-title>
                  {{
                    deliveryRequest.details.numberOfCompletedRoutes || 0
                  }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="4">
        <route-list
          :routes="routes.list"
          :selected-route-id="selectedRoute.id"
          :is-loading="routes.isLoading"
          @select-route="selectRoute"
          @move-to-backlog="moveRouteToBacklog"
        />
      </v-col>

      <v-col cols="8">
        <v-card outlined>
          <v-tabs
            v-model="selectedTab"
            active-class="secondary--text"
            hide-slider
            background-color="transparent"
          >
            <v-tab
              v-for="tab in tabs"
              :key="tab.title"
            >
              <span class="text-capitalize">{{ tab.title }}</span>
              <span
                v-if="tab.count"
                class="mx-1"
              >({{ tab.count }})</span>
            </v-tab>
          </v-tabs>

          <v-divider />

          <v-card-text class="pa-0">
            <div
              v-if="routes.isLoading"
              class="text-center"
            >
              <v-progress-linear indeterminate />
            </div>

            <component
              :is="tabs[selectedTab].component"
              v-if="selectedRoute.id"
              :route-details="selectedRoute"
              :class="tabs[selectedTab].class"
              :route="{...selectedRoute, name: '1st', isFeasibleRoute: true }"
              :locations="selectedRoute && parsePathToLocation(selectedRoute.path)"
              @route-updated="fetchDeliveryRequest(), fetchRoutes()"
            />

            <div
              v-else-if="routes.isLoading"
              class="pa-5"
            >
              Loading route details.
            </div>

            <div
              v-else
              class="text-center pa-5"
            >
              <v-icon
                color="grey"
                size="50"
              >
                mdi-text-box-search-outline
              </v-icon>
              <div class="subtitle-2 my-1">
                No Routes Selected
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="snackbar.isOpen"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>

import {
  routesCollection,
  secureAuthSupplier,
  deliveryRequestsCollection,
} from '@/firebase';
import { humanizeDateFromSeconds, downloadCsv } from '@/utils/misc';
import { parseDeliveryRequest, parseRoute } from '@/utils/documentParser';
import RouteList from '@/components/delivery-request-overview/RouteList.vue';

export default {
  components: {
    RouteList,
    RouteOverview: () => import(
      '@/components/delivery-request-overview/route-overview/RouteOverview'
    ),
    SuggestedRoute: () => import('@/components/route-planner/suggested-routes/SuggestedRoute'),
    JobList: () => import('@/components/delivery-request-overview/route-overview/JobList'),
    PurchaseOrderList: () => import('@/components/delivery-request-overview/route-overview/PurchaseOrderList'),
  },
  data() {
    return {
      deliveryRequestId: this.$route.params.id,
      deliveryRequest: {
        isLoading: true,
        details: {},
      },
      selectedRoute: {},
      routes: {
        isLoading: false,
        list: [],
      },
      snackbar: {
        text: '',
        isOpen: false,
        color: 'green',
      },
      selectedTab: 'route-overview',
      tabs: [
        { title: 'Overview', component: 'route-overview', class: 'px-3' },
        { title: 'Jobs', component: 'job-list', class: '' },
        { title: 'Purchase Orders', component: 'purchase-order-list', class: '' },
        { title: 'Routes', component: 'suggested-route', class: 'px-3' },
        // { title: "Track & Trace" },
        // { title: "Proof of Delivery " },
        // { title: "Invoice History" },
      ],
      isDownloadingBNQCsv: false,
      isDownloadingRoutesCsv: false,
    };
  },
  computed: {
    isAccepted() {
      return (
        this.isDelivered || this.deliveryRequest.details.status === 'Accepted'
      );
    },
    isInProgress() {
      return this.deliveryRequest.details.status === 'IT';
    },
    isDelivered() {
      return this.deliveryRequest.details.status === 'Completed';
    },
  },
  mounted() {
    this.fetchDeliveryRequest();
    this.fetchRoutes();
  },
  methods: {
    humanizeDateFromSeconds,
    fetchDeliveryRequest() {
      deliveryRequestsCollection
        .doc(this.deliveryRequestId)
        .get()
        .then((snapshot) => {
          this.deliveryRequest.details = parseDeliveryRequest(snapshot.data());
          this.deliveryRequest.isLoading = false;
        })
        .catch(() => {
          this.deliveryRequest.isLoading = false;
          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    fetchRoutes() {
      this.routes.isLoading = true;

      secureAuthSupplier(routesCollection)
        .where(
          'delivery_request',
          '==',
          deliveryRequestsCollection.doc(this.deliveryRequestId),
        )
        .get()
        .then((snapshot) => {
          this.routes.list = snapshot.docs.map(parseRoute);

          if (this.selectedRoute.id) {
            this.selectedRoute = this.routes.list
              .find((route) => route.id === this.selectedRoute.id);
          } else if (this.routes.list.length) {
            [this.selectedRoute] = this.routes.list;
          }

          this.routes.isLoading = false;
        })
        .catch(() => {
          this.routes.isLoading = false;
        });
    },
    downloadRoutesCsv() {
      this.isDownloadingRoutesCsv = true;

      const data = {
        delivery_request_id: this.deliveryRequestId,
      };

      this.axios
        .post('download_csv/routes/', data)
        .then((response) => {
          this.isDownloadingRoutesCsv = false;

          downloadCsv(response.data, 'routes');
        })
        .catch(() => {
          this.isDownloadingRoutesCsv = false;

          this.displaySnackbar({
            color: 'red',
            text: 'The CSV could not be downloaded.',
          });
        });
    },
    downloadBNQCsv() {
      this.isDownloadingBNQCsv = true;

      const data = {
        delivery_request_id: this.deliveryRequestId,
      };

      this.axios
        .post('download_csv/bq/', data)
        .then((response) => {
          this.isDownloadingBNQCsv = false;

          downloadCsv(response.data, 'bnq');
        })
        .catch(() => {
          this.isDownloadingBNQCsv = false;

          this.displaySnackbar({
            color: 'red',
            text: 'The CSV could not be downloaded.',
          });
        });
    },
    moveRouteToBacklog(routeIds) {
      this.routes.isLoading = true;

      const data = {
        route_ids: routeIds,
      };
      this.axios
        .post('send_po_to_backlog/', data)
        .then(() => {
          this.routes.isLoading = false;

          this.displaySnackbar({
            text: 'Successfully moved to backlog.',
            color: 'green',
          });

          this.fetchRoutes();
        })
        .catch(() => {
          this.routes.isLoading = false;

          this.displaySnackbar({
            text: 'Could not move to backlog.',
            color: 'red',
          });
        });
    },
    selectRoute(route) {
      this.selectedRoute = route;
      this.selectedTab = 0;
    },
    parsePathToLocation(paths) {
      return paths.map((path) => ({
        ...path,
        lat: path.coords.latitude,
        lng: path.coords.longitude,
      }));
    },
    displaySnackbar({ text, color }) {
      this.snackbar = {
        text,
        color,
        isOpen: true,
      };
    },
  },
};
</script>

<style scoped>

.status-block {
  height:26px;
}

.pointed-end-primary {
  width: 0;
  height: 0;
  border-top: 26px solid transparent;
  border-bottom: 26px solid transparent;
  border-left: 26px solid #2d2f54;
}

.pointed-end-success {
  width: 0;
  height: 0;
  border-top: 26px solid transparent;
  border-bottom: 26px solid transparent;
  border-left: 26px solid #4caf50;
}

.pointed-end-grey {
  width: 0;
  height: 0;
  border-top: 26px solid transparent;
  border-bottom: 26px solid transparent;
  border-left: 26px solid #e0e0e0;
}
</style>
